import { useAppService, useSelectedOrgId } from "@fero/hooks";
import { Stack } from "@mui/material";
import type { WithDbId } from "adl-gen/common/db";
import {
  makeProductDealLegSearchQueryReq,
  type ProductListing,
  type StorageLocationView,
} from "adl-gen/ferovinum/app/api";
import type {
  NumberOfUnits,
  Product,
  StorageLocation,
} from "adl-gen/ferovinum/app/db";
import {
  useWizardStepSetup,
  type WizardContextLoader,
  type WizardStepComponent,
  type WizardStepState,
} from "components/library/components/wizard/wizard";
import type { SelectorInfo } from "components/library/widgets/db-key-selector";
import { ProductQuantityCsvUpload } from "components/library/widgets/product-quantity-csv-upload-button";
import type { ProductQuantityUploadResult } from "components/library/widgets/product-quantity-csv-upload-button/product-quantity-csv-processor";
import { DestinationWarehouseCard } from "components/library/widgets/wizard-card/destination-warehouse-card";
import { ProductLookupTableCard } from "components/library/widgets/wizard-card/product-lookup-table-card";
import { StartingStorageLocationCard } from "components/library/widgets/wizard-card/starting-storage-location-card";
import { FormField } from "components/types/formik-types";
import { GridTableColumnFactory } from "components/widgets/grid-table/grid-table";
import { default as React, useCallback, useMemo } from "react";
import { arrayField, objectField } from "utils/data-field/data-field-builder";
import {
  makeObjectDef,
  type ObjectFields,
} from "utils/data-field/object-field-def";
import { assertNotUndefined } from "utils/hx/util/types";
import {
  add,
  canSellPartialQuantity,
  formatNumberOfUnits,
  formatNumberOfUnits2,
  unitsLabelForUnitType,
} from "utils/model-utils";
import {
  PRODUCT_ROW_DEF,
  type ProductRow,
  STOCK_MOVEMENT_PRODUCT_SELECTION_DATA_DEF,
  type StockMovementProductSelectionContext,
  type StockMovementProductSelectionData,
  type StockMovementWizardContext,
  type StockMovementWizardData,
} from "./stock-movement-wizard-types";

export interface ProductMaybeWithQuantity {
  product: WithDbId<Product>;
  quantity?: NumberOfUnits;
}

const ENRICHED_PRODUCT_ROW_DEF = makeObjectDef<ProductRow>({
  productId: PRODUCT_ROW_DEF.productId,
  quantity: PRODUCT_ROW_DEF.quantity.withUpdatedSchema((schema) =>
    schema.test(
      "max-quantity",
      "Quantity to relocate cannot be more than available quantity",
      (quantity, ctx) => quantity <= ctx.parent.maxQuantity,
    ),
  ),
  maxQuantity: PRODUCT_ROW_DEF.maxQuantity,
});

const ENRICHED_DATA_DEF: Readonly<
  ObjectFields<StockMovementProductSelectionData>
> = Object.freeze({
  startLocation: STOCK_MOVEMENT_PRODUCT_SELECTION_DATA_DEF.startLocation,
  destinationLocation:
    STOCK_MOVEMENT_PRODUCT_SELECTION_DATA_DEF.destinationLocation.withUpdatedSchema(
      (schema) =>
        schema.test(
          "not-equal-from-loc",
          "Cannot be the same as Starting Location",
          (toLoc, ctx) => ctx.parent.startLocation !== toLoc,
        ),
    ),
  products: arrayField<ProductRow>(
    "Products",
    objectField("Product", ENRICHED_PRODUCT_ROW_DEF).required(),
  )
    .updateSchemaWithNonTrivialValidation((s) =>
      s.test(
        "not-empty",
        "Needs at least 1 product entry.",
        (products) => products && products.length > 0,
      ),
    )
    .required(),
});

type ProductSelectionStepState = WizardStepState<
  StockMovementWizardData,
  StockMovementWizardContext,
  "productSelection"
>;

export const useStockMovementProductSelectionContext: WizardContextLoader<
  StockMovementWizardData,
  StockMovementWizardContext,
  "productSelection"
> = async ({
  stepData: initData,
  stepContext: initContext,
}): Promise<ProductSelectionStepState> => {
  const appService = useAppService();
  const orgId = assertNotUndefined(useSelectedOrgId());

  const productIds = initData?.products?.map((p) => p.productId) ?? [];
  if (initContext && productIds.every((id) => initContext.productInfos[id])) {
    return { stepData: initData ?? {}, stepContext: initContext };
  }
  // otherwise load stepContext
  const hasStockLocations = await appService.getStorageLocations({
    orgId,
    selector: { kind: "hasStock" },
  });
  const associatedAndPublicLocations = await appService.getStorageLocations({
    orgId,
    selector: { kind: "associatedAndPublic" },
  });

  const startLocation = initData?.startLocation;
  const listings =
    startLocation && productIds.length > 0
      ? await appService.getOrgProductListingsAtLocation({
          organisationId: orgId,
          storageLocationId: startLocation,
          productIds,
        })
      : [];
  const stepContext: StockMovementProductSelectionContext = {
    hasStockLocations,
    associatedAndPublicLocations,
    productInfos: listings.reduce((acc, listing) => {
      return {
        ...acc,
        [listing.productWithId.id]: extractProductInfo(listing),
      };
    }, {}),
  };
  return { stepData: initData ?? {}, stepContext };
};

export const StockMovementProductSelectionStep: WizardStepComponent<
  StockMovementWizardData,
  StockMovementWizardContext,
  "productSelection"
> = ({ stepData, stepContext, registerDataGetter }) => {
  const appService = useAppService();
  const organisationId = assertNotUndefined(useSelectedOrgId());
  const [isLoading, setIsLoading] = React.useState(false);

  const productInfos = useMemo(() => stepContext.productInfos, [stepContext]);

  const initData: StockMovementProductSelectionData = useMemo(() => {
    const startLocation = stepData?.startLocation ?? "";
    const destinationLocation = stepData?.destinationLocation ?? "";
    const cachedProducts = stepData?.products ?? [];
    const products = startLocation ? cachedProducts : [];
    return { startLocation, destinationLocation, products };
  }, [stepData]);

  const enrichedDataFieldDef = objectField("", ENRICHED_DATA_DEF).required();

  const { form } = useWizardStepSetup<
    StockMovementProductSelectionData,
    StockMovementProductSelectionContext
  >(initData, enrichedDataFieldDef, registerDataGetter);

  const productTableColumns = (() => {
    const factory = new GridTableColumnFactory<ProductRow>();
    return [
      factory.makeRemovalColumn({
        match: (r1, r2) => r1.productId === r2.productId,
      }),
      factory.makeProductSummaryColumn({
        header: "Product",
        name: "productSummary",
        getter: (row) => productInfos[row.productId].productSummary,
      }),
      factory.makeTextColumn({
        header: "Available",
        name: "available",
        getter: (row) =>
          formatNumberOfUnits2(
            row.maxQuantity,
            productInfos[row.productId].unitType,
          ),
      }),
      factory.makeEditableNumberColumn({
        key: "quantity",
        header: "Quantity to Relocate",
        displayFormatter: (row) =>
          formatNumberOfUnits2(
            row.quantity,
            productInfos[row.productId].unitType,
          ),
        editableCheck: (row) =>
          canSellPartialQuantity(productInfos[row.productId].unitType),
        endAdornmentTextProvider: (row) =>
          unitsLabelForUnitType(productInfos[row.productId].unitType),
        inputParams: (row) => {
          return {
            range: {
              min: 0,
              max: row.maxQuantity,
              step: 1,
            },
          };
        },
      }),
    ];
  })();

  const productListingCache: Record<string, ProductListing> = useMemo(
    () => ({}),
    [],
  );
  const fetchProductView = useCallback(
    async (productIds: string[]) => {
      const missingIds = productIds.filter((id) => !productListingCache[id]);

      if (missingIds.length) {
        (
          await appService.getOrgProductListingsAtLocation({
            organisationId,
            storageLocationId: form.values.startLocation,
            productIds: missingIds,
          })
        ).forEach(
          (product) =>
            (productListingCache[product.productWithId.id] = product),
        );
      }

      return productIds.reduce(
        (map, id) => (
          productListingCache[id] && (map[id] = productListingCache[id]), map
        ),
        {} as Record<string, ProductListing>,
      );
    },
    [
      appService,
      form.values.startLocation,
      organisationId,
      productListingCache,
    ],
  );

  const validateProduct = useCallback(
    (pq: ProductMaybeWithQuantity, productMatch?: ProductListing): string[] => {
      if (!productMatch) {
        return [`Product ${pq.product.value.code} not found in inventory`];
      }

      const errors = [];

      const zeroUnits = {
        kind: productMatch.dealLegs[0].totalReservableQuantity.kind,
        value: 0,
      };
      const maxQuantity = productMatch.dealLegs.reduce(
        (acc, leg) => add(acc, leg.totalReservableQuantity),
        zeroUnits,
      );

      // if 0 quantity is provided, use max quantity
      const quantity =
        pq.quantity?.value === 0 ? maxQuantity : (pq.quantity ?? maxQuantity);
      if (quantity.kind !== maxQuantity.kind) {
        errors.push(
          `Product ${pq.product.value.code}: has different units of measure for quantity (${quantity.kind}) and max quantity (${maxQuantity.kind})`,
        );
      }

      if (quantity.value > maxQuantity.value) {
        errors.push(
          `Product ${pq.product.value.code}: Quantity (${formatNumberOfUnits(
            quantity,
          )}) exceeds max quantity (${formatNumberOfUnits(maxQuantity)})`,
        );
      }

      return errors;
    },
    [],
  );

  const onAddProduct = useCallback(
    async (productQuantities: ProductMaybeWithQuantity[]): Promise<void> => {
      setIsLoading(true);
      const productMap = new Map(
        form.values.products.map((product) => [product.productId, product]),
      );
      const productViewMap = await fetchProductView(
        productQuantities.map((pq) => pq.product.id),
      );
      const errors: string[] = [];

      productQuantities.forEach((pq) => {
        const productId = pq.product.id;
        productInfos[productId] = extractProductInfo(pq.product);
        const productView = productViewMap[productId];

        const validationErrors = validateProduct(pq, productView);
        if (validationErrors.length > 0) {
          errors.push(...validationErrors);
          return;
        }

        const zeroUnits = {
          kind: productView.dealLegs[0].totalReservableQuantity.kind,
          value: 0,
        };
        const maxQuantity = productView.dealLegs.reduce(
          (acc, leg) => add(acc, leg.totalReservableQuantity),
          zeroUnits,
        );
        const quantity = pq.quantity ?? maxQuantity;

        productMap.set(productId, {
          productId: productId,
          quantity: quantity.value,
          maxQuantity: maxQuantity.value,
        });
      });

      if (errors.length > 0) {
        setIsLoading(false);
        throw new Error(errors.join("\n"));
      }

      form.setFieldValue("products", Array.from(productMap.values()));
      setIsLoading(false);
    },
    [fetchProductView, form, productInfos, validateProduct],
  );

  const postInputCsvUploadValidator = useCallback(
    async (uploadResult: ProductQuantityUploadResult) => {
      setIsLoading(true);
      const newResult: ProductQuantityUploadResult = {
        ...uploadResult,
        success: [...uploadResult.success],
        errors: [...uploadResult.errors],
      };

      const productQuantities = uploadResult.success.map((p) => p.value);
      const productsMap = await fetchProductView(
        productQuantities.map((pq) => pq.product.id),
      );
      newResult.success = newResult.success.filter((pq) => {
        const errors = validateProduct(
          pq.value,
          productsMap[pq.value.product.id],
        );
        if (errors.length > 0) {
          newResult.errors.push({
            rowNumber: pq.rowNumber,
            value: {
              rowIdentifier: pq.value.product.value.code,
              errors,
            },
          });
          return false; // Exclude from success
        }
        return true; // Keep in success
      });

      setIsLoading(false);
      return newResult;
    },
    [fetchProductView, validateProduct],
  );

  const searchProducts = useCallback(
    // todo search by location
    async (searchTerm: string) => {
      if (!form.values.startLocation) return [];
      return await appService.productDealLegSearch(
        makeProductDealLegSearchQueryReq({
          organisationId,
          searchTerm,
          storageLocationId: form.values.startLocation,
          availabilityOption: "availableAndPending",
        }),
      );
    },
    [appService, form.values.startLocation, organisationId],
  );

  const productsField = useMemo(
    () => new FormField<ProductRow[]>(form, "products"),
    [form],
  );
  const getProductsByCode = useCallback(
    async (productCodes: string[]) => {
      setIsLoading(true);
      const result = await appService.getOrgExistingProducts({
        productCodes,
        organisationId,
      });
      setIsLoading(false);
      return result;
    },
    [appService, organisationId],
  );

  return (
    <Stack spacing={7} justifyContent="space-between">
      <StartingStorageLocationCard
        fieldInfo={{ form, fieldKey: "startLocation" }}
        fieldDef={STOCK_MOVEMENT_PRODUCT_SELECTION_DATA_DEF.startLocation}
        options={mapToStorageLocationInfo(stepContext.hasStockLocations)}
        onValueChange={() => {
          form.setFieldValue("products", []);
        }}
      />
      <ProductLookupTableCard
        title={"Stock Selection"}
        subtitle={
          "Please select the stock you would like to move to a new location."
        }
        productTableColumns={productTableColumns}
        inputRowsField={productsField}
        searchProducts={searchProducts}
        onAddProduct={(product) => onAddProduct([{ product }])}
        uniqueProductCodes={
          new Set(
            form.values.products.map(
              (row) => productInfos[row.productId].productSummary.code,
            ),
          )
        }
        isLoading={isLoading}
        tableAction={
          <ProductQuantityCsvUpload
            buttonLabel={"Upload Product Quantities"}
            getProductsByCode={getProductsByCode}
            onSuccessfulUpload={onAddProduct}
            postUploadValidator={postInputCsvUploadValidator}
          />
        }
      />
      <DestinationWarehouseCard
        fieldInfo={{ form, fieldKey: "destinationLocation" }}
        fieldDef={STOCK_MOVEMENT_PRODUCT_SELECTION_DATA_DEF.startLocation}
        options={mapToStorageLocationInfo(
          stepContext.associatedAndPublicLocations,
        )}
      />
    </Stack>
  );
};

const mapToStorageLocationInfo = (
  locs: StorageLocationView[],
): SelectorInfo<StorageLocation>[] =>
  locs.map((loc) => ({
    id: loc.id,
    displayValue: loc.storageLocationName,
    disabled: loc.country !== "GB",
  }));

function extractProductInfo(source: WithDbId<Product> | ProductListing) {
  const productWithId =
    "productWithId" in source ? source.productWithId : source;
  return {
    productSummary: productWithId.value,
    unitType: productWithId.value.unitType,
  };
}
